import { useEffect, useRef, useState } from 'react'
import {
    ChevronDownIcon,
    CircleStackIcon,
    Cog6ToothIcon,
    CreditCardIcon,
    DocumentTextIcon,
    FolderIcon,
    ListBulletIcon,
    RectangleGroupIcon,
    ShieldCheckIcon,
    SparklesIcon,
    UserCircleIcon,
} from '@heroicons/react/24/outline'
import { useLocation, useMatch } from 'react-router-dom'
import { CustomLink } from './sidebarIcon'

interface SidebarSubMenuProps {
    icon: any
    label: string
    route: string
    children: {
        title: string
        route: string
        icon?: any
    }[]
    disabled?: boolean
    collapsed?: boolean
    forceOpen?: boolean
}

const getChildIcon = (title: string) => {
    const value = title.toLowerCase()

    if (value.includes('user')) return UserCircleIcon
    if (value.includes('role') || value.includes('permission')) return ShieldCheckIcon
    if (value.includes('payment')) return CreditCardIcon
    if (value.includes('resource') || value.includes('document') || value.includes('html')) return DocumentTextIcon
    if (value.includes('setting') || value.includes('general')) return Cog6ToothIcon
    if (value.includes('template') || value.includes('widget')) return RectangleGroupIcon
    if (value.includes('language') || value.includes('category')) return ListBulletIcon
    if (value.includes('ai') || value.includes('personal')) return SparklesIcon
    if (value.includes('report') || value.includes('logger')) return CircleStackIcon

    return FolderIcon
}

export const SidebarSubMenu: React.FC<SidebarSubMenuProps> = ({
    icon: Icon,
    label,
    route,
    children,
    disabled,
    collapsed = false,
    forceOpen = false,
}) => {
    const [open, setOpen] = useState(false)
    const [hoverOpen, setHoverOpen] = useState(false)
    const [submenuTop, setSubmenuTop] = useState(0)
    const { pathname } = useLocation()
    const itemRef = useRef<HTMLDivElement>(null)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const match = useMatch({ path: route, end: false })

    const clearCloseTimer = () => {
        if (!closeTimer.current) return
        clearTimeout(closeTimer.current)
        closeTimer.current = null
    }

    const openHoverMenu = () => {
        clearCloseTimer()
        if (!itemRef.current) return

        const rect = itemRef.current.getBoundingClientRect()
        const submenuHeight = children.length * 34 + 50
        const spaceBelow = window.innerHeight - rect.bottom

        setSubmenuTop(spaceBelow < submenuHeight ? Math.max(8, rect.bottom - submenuHeight) : rect.top)
        setHoverOpen(true)
    }

    const closeHoverMenu = () => {
        clearCloseTimer()
        closeTimer.current = setTimeout(() => setHoverOpen(false), 180)
    }

    const isActive = !!match
    const isExpanded = open || forceOpen

    useEffect(() => {
        if (isActive) {
            setOpen(true)
        }
    }, [isActive, pathname])

    const buttonStyle = disabled
        ? collapsed
            ? 'mx-1 my-0.5 block w-[calc(100%-0.5rem)] rounded-xl px-2 py-2.5 text-gray-400 dark:text-gray-600 pointer-events-none select-none'
            : 'mx-1.5 my-0.5 block w-[calc(100%-0.75rem)] rounded-lg px-2.5 py-2 text-gray-400 dark:text-gray-600 pointer-events-none select-none'
        : isActive
            ? collapsed
                ? 'mx-1 my-0.5 block w-[calc(100%-0.5rem)] rounded-xl bg-blue-600 px-2 py-2.5 text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:bg-blue-600 dark:bg-blue-500 dark:text-white'
                : 'group mx-1.5 my-0.5 block w-[calc(100%-0.75rem)] rounded-xl bg-white px-2.5 py-2.5 text-blue-700 shadow-sm ring-1 ring-blue-100 transition-all duration-200 hover:bg-white hover:text-blue-700 dark:bg-slate-900 dark:text-blue-300 dark:ring-blue-900/70'
            : collapsed
                ? 'mx-1 my-0.5 block w-[calc(100%-0.5rem)] rounded-xl px-2 py-2.5 text-slate-600 transition-all duration-200 hover:bg-white hover:text-blue-700 hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300'
                : 'group mx-1.5 my-0.5 block w-[calc(100%-0.75rem)] rounded-xl px-2.5 py-2.5 text-slate-700 transition-all duration-200 hover:bg-white hover:text-blue-700 hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300'

    return (
        <div
            ref={itemRef}
            onMouseEnter={collapsed ? openHoverMenu : undefined}
            onMouseLeave={collapsed ? closeHoverMenu : undefined}
            className="relative"
        >
            <button disabled={disabled} onClick={() => setOpen((prev) => !prev)} className={buttonStyle}>
                <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start gap-2.5'}`}>
                    <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${
                            isActive
                                ? collapsed
                                    ? 'text-white'
                                    : 'text-blue-600 dark:text-blue-300'
                                : collapsed
                                    ? ''
                                    : 'bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 dark:bg-slate-700 dark:text-slate-300 dark:group-hover:bg-blue-900/40'
                        }`}
                    >
                        <Icon className="h-4 w-4" />
                    </span>

                    {!collapsed && (
                        <>
                            <span className="min-w-0 flex-1 truncate text-left text-sm font-medium capitalize">
                                {label}
                            </span>
                            <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </>
                    )}
                </div>
            </button>

            {!collapsed && isExpanded && (
                <ul className="mx-4 mb-1 mt-1 border-l border-blue-100 pl-2 dark:border-slate-800">
                    {children.map((child) => {
                        const ChildIcon = child.icon ?? getChildIcon(child.title)

                        return (
                            <li key={child.route}>
                                <CustomLink to={child.route} variant="child">
                                    <ChildIcon className="mr-2 h-4 w-4 shrink-0" />
                                    <span className="truncate">{child.title}</span>
                                </CustomLink>
                            </li>
                        )
                    })}
                </ul>
            )}

            {collapsed && hoverOpen && (
                <div
                    className="fixed left-[52px] z-50 pl-2"
                    style={{ top: submenuTop }}
                    onMouseEnter={clearCloseTimer}
                    onMouseLeave={closeHoverMenu}
                >
                    <div className="w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-500/25 ring-1 ring-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:shadow-black/40 dark:ring-slate-800">
                        <div className="mb-1 flex items-center gap-2 rounded-lg border border-blue-50 bg-blue-50/80 px-2 py-1.5 dark:border-slate-700 dark:bg-slate-700/70">
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm dark:bg-slate-800 dark:text-blue-300">
                                <Icon className="h-4 w-4" />
                            </span>
                            <span className="text-sm font-semibold text-slate-900 dark:text-white">{label}</span>
                        </div>

                        {children.map((child) => {
                            const ChildIcon = child.icon ?? getChildIcon(child.title)
                            const isActive = pathname === child.route

                            return (
                                <CustomLink key={child.route} to={child.route} variant="floating">
                                    <ChildIcon className={`mr-2 h-4 w-4 shrink-0 ${isActive ? 'text-white' : ''}`} />
                                    <span className="truncate">{child.title}</span>
                                </CustomLink>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
